<template>
  <div v-if="rendered" class="def-root">
    <!-- HEADER -->
    <div class="def-header">
      <div class="def-header-icon">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
        >
          <circle cx="12" cy="12" r="3" />
          <path
            d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"
          />
          <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
        </svg>
      </div>
      <div>
        <h1 class="def-title">Definições</h1>
        <p class="def-sub">Configurações gerais da plataforma</p>
      </div>
    </div>

    <!-- TABS -->
    <div class="def-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="def-tab"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        <component
          :is="'svg'"
          v-html="tab.icon"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="def-tab-icon"
        />
        {{ tab.label }}
      </button>
    </div>

    <!-- CONTEÚDO -->
    <div class="def-body">
      <!-- ── TAB: Tipos de cliente ── -->
      <div v-show="activeTab === 'clientes'" class="def-section">
        <div class="def-section-intro">
          <p>
            Define quantas visitas e em quantos dias um cliente tem de fazer
            para ser considerado de cada tipo.
          </p>
        </div>

        <div class="def-table">
          <div class="def-table-head">
            <div class="def-th def-th-tipo">Tipo de cliente</div>
            <div class="def-th def-th-num">Nº de visitas</div>
            <div class="def-th def-th-num">Nº de dias</div>
          </div>

          <div v-for="row in rows" :key="row.tipo" class="def-table-row">
            <div class="def-td def-td-tipo">
              <span
                class="def-tipo-badge"
                :class="'badge-' + row.tipo.toLowerCase()"
                >{{ row.tipo }}</span
              >
            </div>
            <div class="def-td def-td-num">
              <div class="def-input-wrap">
                <input
                  v-model.number="row.visitas"
                  type="number"
                  min="0"
                  class="def-input"
                  placeholder="0"
                />
                <span class="def-input-unit">visitas</span>
              </div>
            </div>
            <div class="def-td def-td-num">
              <div class="def-input-wrap">
                <input
                  v-model.number="row.dias"
                  type="number"
                  min="0"
                  class="def-input"
                  placeholder="0"
                />
                <span class="def-input-unit">dias</span>
              </div>
            </div>
          </div>
        </div>

        <div class="def-footer">
          <button
            class="def-btn def-btn-primary"
            @click="gravarDefinicoesCliente"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            Guardar alterações
          </button>
        </div>
      </div>

      <!-- ── TAB: Refeições ── -->
      <div v-show="activeTab === 'refeicoes'" class="def-section">
        <div class="def-section-intro">
          <p>
            Define os períodos de refeição. Os horários não se podem sobrepor.
          </p>
        </div>

        <div class="def-table">
          <div class="def-table-head">
            <div class="def-th" style="flex: 1">Nome da refeição</div>
            <div class="def-th def-th-time">Hora início</div>
            <div class="def-th def-th-time">Hora fim</div>
            <div class="def-th def-th-del"></div>
          </div>

          <div v-if="refeicoes.length === 0" class="def-empty">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <p>Nenhuma refeição definida. Adiciona uma abaixo.</p>
          </div>

          <div v-for="(ref, idx) in refeicoes" :key="idx" class="def-table-row">
            <div class="def-td" style="flex: 1">
              <input
                v-model="ref.nome"
                type="text"
                class="def-input def-input-text"
                placeholder="Ex: Almoço, Jantar…"
              />
            </div>
            <div class="def-td def-td-time">
              <input
                v-model="ref.min"
                type="time"
                class="def-input def-input-time"
              />
            </div>
            <div class="def-td def-td-time">
              <input
                v-model="ref.max"
                type="time"
                class="def-input def-input-time"
              />
            </div>
            <div class="def-td def-td-del">
              <button
                class="def-del-btn"
                @click="removerRefeicao(idx)"
                title="Remover"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div class="def-footer def-footer-split">
          <button
            class="def-btn def-btn-ghost"
            @click="adicionarRefeicao"
            :disabled="refeicoes.length >= 6"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            Adicionar refeição
          </button>
          <button class="def-btn def-btn-primary" @click="gravarRefeicoes">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            Guardar alterações
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="def-loading">
    <LoadingOverlay />
  </div>
</template>

<script>
  import LoadingOverlay from "@/components/Loading/LoadingOverlay";
  import { definicoesService } from "@/services";

  export default {
    components: { LoadingOverlay },
    data() {
      return {
        rendered: false,
        activeTab: "clientes",
        tabs: [
          {
            key: "clientes",
            label: "Tipos de cliente",
            icon: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
          },
          {
            key: "refeicoes",
            label: "Refeições",
            icon: '<path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/>',
          },
        ],
        rows: [
          {
            tipo: "Assíduo",
            keyVisitas: "assiduo_visitas",
            keyDias: "assiduo_dias",
            visitas: 6,
            dias: 90,
          },
          {
            tipo: "Frequente",
            keyVisitas: "frequente_visitas",
            keyDias: "frequente_dias",
            visitas: 3,
            dias: 90,
          },
          {
            tipo: "Ocasional",
            keyVisitas: "ocasional_visitas",
            keyDias: "ocasional_dias",
            visitas: 1,
            dias: 90,
          },
        ],
        refeicoes: [],
      };
    },
    methods: {
      async inicio() {
        try {
          const defs = await definicoesService.get();
          this.rows.forEach((r) => {
            r.visitas = parseInt(
              defs.find((d) => d.opt_key === r.keyVisitas)?.value || 0,
            );
            r.dias = parseInt(
              defs.find((d) => d.opt_key === r.keyDias)?.value || 0,
            );
          });
          const refeicoesDefs = await definicoesService.getRefeicoes();
          this.refeicoes = refeicoesDefs.map((d) => {
            let val = {};
            try {
              val = JSON.parse(d.value);
            } catch {}
            return {
              opt_key: d.opt_key,
              nome: val.nome || "",
              min: String(val.min ?? 0).padStart(2, "0") + ":00",
              max: String(val.max ?? 0).padStart(2, "0") + ":00",
            };
          });
        } catch (err) {
          if (err.response?.status === 403) {
            localStorage.removeItem("tokenAbitue");
            this.$router.push("/login");
          } else console.error("Erro ao carregar definições:", err);
        }
      },
      async gravarDefinicoesCliente() {
        try {
          const payload = {};
          this.rows.forEach((r) => {
            payload[r.keyVisitas] = r.visitas;
            payload[r.keyDias] = r.dias;
          });
          await definicoesService.update(payload);
          this.$notify({
            type: "success",
            title: "Guardado",
            message: "Definições guardadas com sucesso.",
          });
        } catch {
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível guardar.",
          });
        }
      },
      validarRefeicoes() {
        const toMin = (t) => {
          const [h, m] = t.split(":").map(Number);
          return h * 60 + m;
        };
        const norm = (s, e) => (e <= s ? [s, e + 1440] : [s, e]);
        for (let i = 0; i < this.refeicoes.length; i++) {
          const [s1, e1] = norm(
            toMin(this.refeicoes[i].min),
            toMin(this.refeicoes[i].max),
          );
          for (let j = i + 1; j < this.refeicoes.length; j++) {
            const [s2, e2] = norm(
              toMin(this.refeicoes[j].min),
              toMin(this.refeicoes[j].max),
            );
            if (s1 < e2 && s2 < e1)
              return `"${this.refeicoes[i].nome}" sobrepõe-se a "${this.refeicoes[j].nome}"`;
          }
        }
        return null;
      },
      async gravarRefeicoes() {
        const erro = this.validarRefeicoes();
        if (erro) {
          this.$notify({
            type: "danger",
            title: "Horários sobrepostos",
            message: erro,
          });
          return;
        }
        try {
          const payload = this.refeicoes.map((r, i) => ({
            opt_key: r.opt_key || `refeicao_${i + 1}`,
            value: JSON.stringify({
              nome: r.nome,
              min: parseInt(r.min.split(":")[0], 10),
              max: parseInt(r.max.split(":")[0], 10),
            }),
          }));
          await definicoesService.updateRefeicoes(payload);
          this.$notify({
            type: "success",
            title: "Guardado",
            message: "Refeições guardadas com sucesso.",
          });
        } catch {
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Não foi possível guardar.",
          });
        }
      },
      adicionarRefeicao() {
        if (this.refeicoes.length >= 6) {
          this.$notify({
            type: "warning",
            title: "Limite atingido",
            message: "Máximo de 6 refeições.",
          });
          return;
        }
        this.refeicoes.push({ nome: "", min: "00:00", max: "00:00" });
      },
      async removerRefeicao(idx) {
        const ref = this.refeicoes[idx];
        if (ref.opt_key) {
          try {
            await definicoesService.deleteRefeicao(ref.opt_key);
          } catch {
            this.$notify({
              type: "danger",
              title: "Erro",
              message: "Não foi possível remover.",
            });
            return;
          }
        }
        this.refeicoes.splice(idx, 1);
      },
    },
    async mounted() {
      await this.inicio();
      this.rendered = true;
    },
  };
</script>

<style scoped>
  .def-root {
    padding: 28px 28px 48px;
    font-family: "DM Sans", "Outfit", system-ui, sans-serif;
  }

  /* HEADER */
  .def-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 28px;
  }
  .def-header-icon {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    background: #ff825d;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 4px 14px rgba(255, 130, 93, 0.3);
  }
  .def-title {
    font-size: 20px;
    font-weight: 800;
    color: #1a1210;
    margin: 0;
    letter-spacing: -0.4px;
  }
  .def-sub {
    font-size: 13px;
    color: #b0a8a0;
    margin: 3px 0 0;
  }

  /* TABS */
  .def-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 2px solid #f0ece8;
    margin-bottom: 28px;
  }
  .def-tab {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 10px 16px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 13.5px;
    font-weight: 500;
    color: #9a908a;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    border-radius: 8px 8px 0 0;
    transition: all 0.15s;
    font-family: inherit;
  }
  .def-tab:hover {
    color: #3a3430;
    background: #faf8f6;
  }
  .def-tab.active {
    color: #ff825d;
    font-weight: 700;
    border-bottom-color: #ff825d;
    background: none;
  }
  .def-tab-icon {
    flex-shrink: 0;
  }

  /* SECTION */
  .def-section-intro p {
    font-size: 13.5px;
    color: #8a857e;
    margin: 0 0 20px;
    line-height: 1.6;
  }

  /* TABLE */
  .def-table {
    background: #fff;
    border: 1.5px solid #f0ece8;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.04);
    margin-bottom: 20px;
  }
  .def-table-head {
    display: flex;
    align-items: center;
    padding: 10px 18px;
    background: #faf8f6;
    border-bottom: 1px solid #f0ece8;
  }
  .def-th {
    font-size: 11px;
    font-weight: 700;
    color: #b0a8a0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .def-th-tipo {
    flex: 1;
  }
  .def-th-num {
    width: 200px;
    text-align: center;
  }
  .def-th-time {
    width: 160px;
    text-align: center;
  }
  .def-th-del {
    width: 48px;
  }

  .def-table-row {
    display: flex;
    align-items: center;
    padding: 14px 18px;
    border-bottom: 1px solid #f8f5f2;
    transition: background 0.1s;
  }
  .def-table-row:last-child {
    border-bottom: none;
  }
  .def-table-row:hover {
    background: #fdfcfb;
  }

  .def-td {
    display: flex;
    align-items: center;
  }
  .def-td-tipo {
    flex: 1;
  }
  .def-td-num {
    width: 200px;
    justify-content: center;
  }
  .def-td-time {
    width: 160px;
    justify-content: center;
  }
  .def-td-del {
    width: 48px;
    justify-content: flex-end;
  }

  /* Badges tipo */
  .def-tipo-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12.5px;
    font-weight: 700;
  }
  .badge-assíduo {
    background: #e8faf2;
    color: #0a7a4a;
  }
  .badge-frequente {
    background: #fff0eb;
    color: #c04820;
  }
  .badge-ocasional {
    background: #f5f2ef;
    color: #7a6e68;
  }

  /* Inputs */
  .def-input-wrap {
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .def-input {
    width: 70px;
    height: 36px;
    border: 1.5px solid #e8e4e0;
    border-radius: 9px;
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    color: #1a1210;
    outline: none;
    background: #fff;
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .def-input:focus {
    border-color: #ff825d;
    box-shadow: 0 0 0 3px rgba(255, 130, 93, 0.12);
  }
  .def-input-unit {
    font-size: 12px;
    color: #b0a8a0;
    white-space: nowrap;
  }

  .def-input-text {
    width: 100%;
    text-align: left;
    font-size: 14px;
    font-weight: 400;
    padding: 0 12px;
  }
  .def-input-time {
    width: 100px;
    font-size: 13.5px;
    font-weight: 600;
  }

  /* Empty */
  .def-empty {
    padding: 32px 20px;
    text-align: center;
    color: #c4bdb8;
  }
  .def-empty svg {
    display: block;
    margin: 0 auto 10px;
    opacity: 0.4;
  }
  .def-empty p {
    font-size: 13px;
    margin: 0;
  }

  /* Delete btn */
  .def-del-btn {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    border: none;
    cursor: pointer;
    background: #f5f2ef;
    color: #b0a8a0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.12s;
  }
  .def-del-btn:hover {
    background: #fef0ef;
    color: #c04820;
  }

  /* Footer */
  .def-footer {
    display: flex;
    justify-content: flex-end;
  }
  .def-footer-split {
    justify-content: space-between;
  }

  .def-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 20px;
    border: none;
    border-radius: 11px;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;
  }
  .def-btn-primary {
    background: #ff825d;
    color: #fff;
    box-shadow: 0 2px 8px rgba(255, 130, 93, 0.3);
  }
  .def-btn-primary:hover {
    background: #e86b45;
    transform: translateY(-1px);
  }
  .def-btn-ghost {
    background: #f7f4f1;
    color: #5a534f;
    border: 1.5px solid #e8e4e0;
  }
  .def-btn-ghost:hover {
    background: #f0ece8;
  }
  .def-btn-ghost:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .def-loading {
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 600px) {
    .def-root {
      padding: 16px 12px 40px;
    }
    .def-table-head {
      display: none;
    }
    .def-table-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
    .def-td-num,
    .def-td-time {
      width: 100%;
    }
    .def-th-num,
    .def-th-time {
      width: auto;
    }
    .def-input {
      width: 80px;
    }
    .def-input-text,
    .def-input-time {
      width: 100%;
    }
  }
</style>
